import styled, { css } from 'styled-components';
import { BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { BaseDealBreakPoints } from '../../../utils/useBaseDealBreakpoint';
import type { BaseDealBreakpoint } from '../../../utils/useBaseDealBreakpoint';
import { BreakPoint } from '@makemydeal/dr-activities-common';

export enum Sections {
    Left = 'LEFT',
    Right = 'RIGHT'
}
interface SectionProps {
    offerType: string;
    sm?: number;
    md?: number;
    lg?: number;
    section: string;
    breakpoints: BaseDealBreakpoint;
    isPaymentGridEnabled?: boolean;
    isDealCentralExperience?: boolean;
    isSingleColumn?: boolean;
}

export const BaseDealSummarySectionContainer = styled.div<{
    breakpoints: BaseDealBreakpoint;
    offerType: string;
    isPaymentGridEnabled: boolean;
    isDealCentralExperience?: boolean;
    isDealCentralMultiScenario?: boolean;
}>`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 4rem 2rem;
    width: 100%;

    flex-direction: ${({ isPaymentGridEnabled, isDealCentralMultiScenario }) =>
        isPaymentGridEnabled || isDealCentralMultiScenario ? 'column' : 'row'};

    @media (max-width: 900px) {
        width: 100%;
        flex-direction: column;
    }

    @media (max-width: ${({ breakpoints, offerType }) => (offerType === LEASE ? breakpoints.MEDIUM : breakpoints.LG)}) {
        flex-direction: column;
    }
    @media (max-width: ${({ breakpoints }) => breakpoints.MD}) {
        width: 100%;
    }

    @media (min-width: ${BreakPoint.SMALL}) and (max-width: ${BaseDealBreakPoints.LG}) {
        flex-direction: ${(props) => {
            if (props.isDealCentralExperience && !props.isDealCentralMultiScenario) {
                if (props.offerType === CASH) {
                    return 'column';
                } else if (props.offerType !== LEASE) {
                    return 'row';
                }
            }
            return 'column';
        }};
    }
`;

export const CompactBaseDealSummarySectionContainer = styled(BaseDealSummarySectionContainer)`
    justify-content: flex-start;
    gap: 0.5rem;
`;

export const BaseDealSummaryFieldsContainer = styled.div<SectionProps>`
    width: ${({ offerType, section, isPaymentGridEnabled, isSingleColumn }) => {
        if (isPaymentGridEnabled || isSingleColumn) {
            return '100%';
        }

        if (offerType === FINANCE || offerType === BALLOON) {
            return '50%'; // For finance and balloon, both left and right summary sections should take 50%
        }

        return section === Sections.Left ? '70%' : '30%';
    }};

    @media (max-width: 900px) {
        width: 100%;
        flex-direction: column;
    }

    @media (max-width: ${({ breakpoints, offerType }) => (offerType === LEASE ? breakpoints.MEDIUM : breakpoints.LG)}) {
        width: 100%;
    }

    display: grid;
    gap: 0.8rem 1.14rem;

    // sm
    grid-template-columns: ${({ sm }) => `repeat(${sm}, 1fr)`};

    // md
    @media (min-width: ${({ breakpoints }) => breakpoints.SMALL}) {
        grid-template-columns: ${({ md }) => `repeat(${md}, 1fr)`};
    }

    // lg
    @media (min-width: ${({ breakpoints, offerType }) => (offerType === LEASE ? breakpoints.MEDIUM : breakpoints.XL)}) {
        grid-template-columns: ${({ lg }) => `repeat(${lg}, 1fr)`};
    }
`;

export const CompactBaseDealSummaryFieldsContainer = styled(BaseDealSummaryFieldsContainer)`
    gap: 0.5rem;

    // sm
    grid-template-columns: ${({ sm }) => `repeat(${sm}, minmax(min-content, 1fr))`};

    // md
    @media (min-width: ${({ breakpoints }) => breakpoints.SMALL}) {
        grid-template-columns: ${({ md }) => `repeat(${md}, minmax(min-content, 1fr))`};
    }

    // lg
    @media (min-width: ${({ breakpoints, offerType }) => (offerType === LEASE ? breakpoints.MEDIUM : breakpoints.XL)}) {
        grid-template-columns: ${({ lg }) => `repeat(${lg}, minmax(min-content, 1fr))`};
    }
`;

interface InputFieldProps {
    sm?: number;
    md?: number;
    lg?: number;
    breakpoints: BaseDealBreakpoint;
    offerType: string;
    isFlexWrapper?: boolean;
}

export const SummaryField = styled.div<InputFieldProps>`
    align-self: start;

    // sm
    grid-column: ${({ sm }) => `span ${sm}`};

    // md
    @media (min-width: ${({ breakpoints }) => breakpoints.SMALL}) {
        grid-column: ${({ md }) => `span ${md}`};
    }

    // lg
    @media (min-width: ${({ breakpoints, offerType }) => (offerType === LEASE ? breakpoints.MEDIUM : breakpoints.XL)}) {
        grid-column: ${({ lg }) => `span ${lg}`};
    }

    label {
        font-size: 0.857rem !important;
        white-space: nowrap !important;
        span {
            white-space: normal;
        }
    }

    ${(props) =>
        props.isFlexWrapper &&
        css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            label {
                margin-top: 0px;
                line-height: 1.143rem;
            }
        `}

    .residual-field-container {
        flex: 1;
        .ids-input-layout-vertical {
            display: flex;
            flex-direction: column;
        }
    }

    .base-deal-lease-field {
        display: grid !important;
        align-items: flex-end;
        gap: 0.857rem;
        grid-template-columns: 2fr 1fr;
        @media (min-width: ${({ breakpoints }) => breakpoints.SMALL}) {
            grid-template-columns: 2fr 2fr;
        }
    }
`;

export const CompactSummaryField = styled(SummaryField)`
    min-width: 0;
`;

export const BaseDealSummaryMainContainer = styled.div<{
    breakpoints: BaseDealBreakpoint;
    isBreakdownOpen?: boolean;
}>`
    display: flex;
    width: 100%;
    gap: 1.714rem;
    justify-content: space-between;
    @media (max-width: 1543px) {
        flex-direction: ${({ isBreakdownOpen }) => (isBreakdownOpen ? 'column-reverse' : 'row')};
    }
    @media (max-width: ${({ breakpoints }) => breakpoints.SMALL}) {
        flex-direction: column-reverse;
    }
`;

export const CompactFeesTaxesContainer = styled.div`
    display: flex;
    width: 100%;
`;

export const CompactFeesTaxesItem = styled.div`
    flex: 1;
`;

export const CompactSummaryDivider = styled.hr`
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
`;
